/**
 * 예약 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { bookingService } from '../../services/bookingService';
import { Booking, BookingFilter, BookingStatus } from '../../types/booking';
import { format } from 'date-fns';

const BookingsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<BookingFilter>({
    page: 1,
    limit: 20,
  });
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bookings', filter],
    queryFn: () => bookingService.getBookings(filter),
  });

  const getStatusBadge = (status: BookingStatus) => {
    const statusMap: Record<BookingStatus, { variant: string; label: string }> = {
      pending: { variant: 'warning', label: '대기' },
      confirmed: { variant: 'success', label: '확인됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
      completed: { variant: 'info', label: '완료됨' },
      no_show: { variant: 'secondary', label: '노쇼' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  const columns: Column<Booking>[] = [
    {
      key: 'booking_number',
      label: '예약번호',
      render: (booking) => (
        <span style={{ fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
          {booking.booking_number}
        </span>
      ),
    },
    {
      key: 'guest_name',
      label: '고객명',
      render: (booking) => booking.guest?.name || booking.member?.name || '-',
    },
    {
      key: 'contact',
      label: '연락처',
      render: (booking) => booking.guest?.email || booking.member?.email || '-',
    },
    {
      key: 'visit_date',
      label: '방문일시',
      render: (booking) => (
        <div>
          <div>{format(new Date(booking.visit_date), 'yyyy-MM-dd')}</div>
          <small className="text-muted">{booking.visit_time}</small>
        </div>
      ),
    },
    {
      key: 'product',
      label: '상품',
      render: (booking) => booking.hall?.name || booking.package?.name || '-',
    },
    {
      key: 'visitors',
      label: '인원',
      render: (booking) => {
        const total = booking.adult_count + booking.youth_count + booking.disabled_count + booking.infant_count;
        return `${total}명`;
      },
    },
    {
      key: 'final_amount',
      label: '금액',
      render: (booking) => formatCurrency(booking.final_amount),
    },
    {
      key: 'status',
      label: '상태',
      render: (booking) => getStatusBadge(booking.status),
    },
    {
      key: 'created_at',
      label: '예약일시',
      render: (booking) => format(new Date(booking.created_at), 'yyyy-MM-dd HH:mm'),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: 'pending', label: '대기' },
        { value: 'confirmed', label: '확인됨' },
        { value: 'cancelled', label: '취소됨' },
        { value: 'completed', label: '완료됨' },
        { value: 'no_show', label: '노쇼' },
      ],
    },
    {
      name: 'visit_date',
      label: '방문일',
      type: 'dateRange',
    },
    {
      name: 'booking_number',
      label: '예약번호',
      type: 'text',
      placeholder: '예약번호 검색',
    },
    {
      name: 'guest_name',
      label: '고객명',
      type: 'text',
      placeholder: '고객명 검색',
    },
  ];

  const handleFilterChange = (newFilter: Record<string, any>) => {
    setFilter({
      ...filter,
      ...newFilter,
      page: 1, // 필터 변경 시 첫 페이지로
    });
  };

  const handlePageChange = (page: number) => {
    setFilter({ ...filter, page });
  };

  const handleRowClick = (booking: Booking) => {
    navigate(`/admin/bookings/${booking.id}`);
  };

  const handleStatusChange = async (booking: Booking, newStatus: BookingStatus) => {
    try {
      await bookingService.updateBookingStatus(booking.id, newStatus);
      refetch();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleCancel = async () => {
    if (!selectedBooking) return;
    
    try {
      await bookingService.cancelBooking(selectedBooking.id);
      setShowCancelModal(false);
      setSelectedBooking(null);
      refetch();
    } catch (error) {
      console.error('취소 실패:', error);
      alert('예약 취소에 실패했습니다.');
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>예약 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/bookings/calendar')}>
            캘린더 보기
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.bookings || []}
              loading={isLoading}
              emptyMessage="예약 내역이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>

        {/* 취소 확인 모달 */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>예약 취소 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            정말로 이 예약을 취소하시겠습니까?
            {selectedBooking && (
              <div className="mt-3">
                <p><strong>예약번호:</strong> {selectedBooking.booking_number}</p>
                <p><strong>고객명:</strong> {selectedBooking.guest?.name || selectedBooking.member?.name}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              닫기
            </Button>
            <Button variant="danger" onClick={handleCancel}>
              취소하기
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default BookingsList;

